import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { api, ApiError } from '../../lib/api'

type State = 'ouverture' | 'echec'

/**
 * Cible du lien magique : `/connexion?jeton=…`.
 *
 * La note de la planche 01 demande que « le lien profond ouvre l'app directement sur l'étape
 * suivante ». Un lien cliqué dans une messagerie ouvre le navigateur et pas nécessairement la
 * PWA installée : la session est valide dans les deux cas, mais on le signale plutôt que de
 * laisser l'utilisateur croire qu'il est au mauvais endroit.
 */
export function ConnexionRoute() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState<State>('ouverture')
  const [message, setMessage] = useState('')

  const token = params.get('jeton')

  useEffect(() => {
    if (!token) {
      setState('echec')
      setMessage("Ce lien est incomplet. Demandez-en un nouveau.")
      return
    }

    let cancelled = false

    api
      .openSession(token)
      .then(() => {
        if (!cancelled) {
          // `replace` : le jeton ne doit pas rester dans l'historique de navigation.
          void navigate('/', { replace: true })
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return
        }
        setState('echec')
        setMessage(
          cause instanceof ApiError
            ? cause.message
            : "La connexion a échoué. Réessayez dans un instant.",
        )
      })

    return () => {
      cancelled = true
    }
  }, [token, navigate])

  return (
    <main
      className="mx-auto flex min-h-dvh flex-col justify-center bg-surface"
      style={{ maxWidth: 'var(--layout-column)', paddingInline: 'var(--layout-gutter)' }}
    >
      {state === 'ouverture' ? (
        // Jamais de roue qui tourne, jamais d'écran blanc (section 03.6).
        <p className="text-ink-muted" style={{ fontSize: 17 }}>
          Ouverture de votre session…
        </p>
      ) : (
        <>
          <h1 className="font-serif text-screen">
            Lien expiré.
          </h1>
          <p className="mt-4 text-ink-muted" style={{ fontSize: 17, lineHeight: 1.5 }}>
            {message}
          </p>
          <button
            type="button"
            onClick={() => void navigate('/', { replace: true })}
            className="mt-8 w-full bg-accent text-card-title font-semibold text-card"
            style={{ height: 56, borderRadius: 'var(--radius-button)' }}
          >
            Demander un nouveau lien
          </button>
        </>
      )}
    </main>
  )
}
